// components/portal/useInventoryList.ts
import { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { InventoryEntityState, InventoryEntityType, InventoryRegistry, Pagination } from '#types/types';

// --- Utilitaires purs extraits de Table.tsx ---
export const stableSerialize = (value: unknown): string => {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

const computeLocalTableQuerySignature = (
  entityType: string,
  filters: Record<string, unknown>,
  limit: number,
  offset: number
): string => `${entityType}|${limit}|${offset}|${stableSerialize(filters)}`;

const LOCAL_TABLE_CACHE_TTL_MS = 30 * 1000;
const LOCAL_SORT_DEFAULT_DIR: 'asc' | 'desc' = 'desc';

const LOCAL_FIELD_ALIASES: Record<string, string[]> = {
  name: ['name', 'title', 'brandName', 'unitIdentifier'],
  country: ['country', 'countryCode'],
  assetType: ['assetType', 'type'],
  owner: ['owner', 'ownerName', 'portfolioName'],
  status: ['status'],
  createdAt: ['createdAt'],
  updatedAt: ['updatedAt'],
  fitScore: ['fitScore', 'score'],
  NAME: ['name', 'title', 'brandName'],
  CREATED: ['createdAt'],
  CREATED_AT: ['createdAt'],
  UPDATED: ['updatedAt'],
  UPDATED_AT: ['updatedAt'],
  STATUS: ['status'],
  SCORE: ['score', 'fitScore', 'intentScore', 'financialScore', 'plausibilityScore'],
  INTENT: ['intentScore'],
  AREA: ['areaSqm', 'areaSqft', 'surface', 'surfaceArea'],
  RENT: ['rentAmount', 'askingRent', 'monthlyRent', 'annualRent'],
  PROPERTY: ['propertyName', 'propertyId'],
  FLOOR: ['floorName', 'floorId', 'floor'],
  IDENTIFIER: ['unitIdentifier', 'identifier', 'name'],
};

const toCamelCase = (value: string): string =>
  value.toLowerCase().replace(/_([a-z0-9])/g, (_, group: string) => group.toUpperCase());

const resolveFieldCandidates = (key: string): string[] => {
  const trimmed = key.trim();
  if (!trimmed) return [];
  const lower = trimmed.toLowerCase();
  const camel = toCamelCase(trimmed);
  const upper = trimmed.toUpperCase();
  const aliased = [
    ...(LOCAL_FIELD_ALIASES[trimmed] || []),
    ...(LOCAL_FIELD_ALIASES[lower] || []),
    ...(LOCAL_FIELD_ALIASES[camel] || []),
    ...(LOCAL_FIELD_ALIASES[upper] || []),
  ];
  return Array.from(new Set([trimmed, lower, camel, upper, ...aliased]));
};

export const resolveRecordValue = (record: Record<string, unknown>, key: string): unknown => {
  const candidates = resolveFieldCandidates(key);
  for (const candidate of candidates) {
    if (candidate in record) return record[candidate];
  }
  return undefined;
};

const collectSearchTerms = (value: unknown, depth = 0): string[] => {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') return value ? [value] : [];
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)];
  if (Array.isArray(value)) {
    if (depth >= 2) return [];
    return value.flatMap((entry) => collectSearchTerms(entry, depth + 1));
  }
  if (typeof value === 'object') {
    if (depth >= 2) return [];
    return Object.values(value as Record<string, unknown>).flatMap((entry) => collectSearchTerms(entry, depth + 1));
  }
  return [];
};

const parseComparableDate = (value: string): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeComparableValue = (value: unknown): { rank: number; value: number | string } => {
  if (value === null || value === undefined) return { rank: 4, value: '' };
  if (typeof value === 'number') return { rank: 0, value };
  if (typeof value === 'boolean') return { rank: 1, value: value ? 1 : 0 };
  if (typeof value === 'string') {
    const asDate = parseComparableDate(value);
    if (asDate !== null) return { rank: 0, value: asDate };
    return { rank: 2, value: value.toLowerCase() };
  }
  if (value instanceof Date) return { rank: 0, value: value.getTime() };
  return { rank: 3, value: stableSerialize(value) };
};

/**
 * Stable local sort on the record's CURRENT values, empty values last in both directions. Also run
 * on a queried page: a live run fills scores in after the page loaded, and rows must climb as they
 * are scored instead of pending ones staying on top (owner, 2026-09-28). A key the record does not
 * carry compares equal everywhere, so the server's order is kept.
 */
export const sortItemsLocally = <T,>(items: T[], sortBy: string, sortDir: unknown, accessor?: (item: T) => unknown): T[] => {
  const direction = sortDir === 'asc' ? 1 : -1;
  const read = (item: T) => (accessor ? accessor(item) : resolveRecordValue(item as any, sortBy));
  return items.map((item, index) => ({ item, index, value: read(item) })).sort((left, right) => {
    const leftEmpty = left.value === null || left.value === undefined;
    const rightEmpty = right.value === null || right.value === undefined;
    if (leftEmpty !== rightEmpty) return leftEmpty ? 1 : -1;
    const comparison = compareUnknownValues(left.value, right.value);
    return comparison === 0 ? left.index - right.index : comparison * direction;
  }).map((entry) => entry.item);
};

const compareUnknownValues = (left: unknown, right: unknown): number => {
  const normalizedLeft = normalizeComparableValue(left);
  const normalizedRight = normalizeComparableValue(right);
  if (normalizedLeft.rank !== normalizedRight.rank) {
    return normalizedLeft.rank - normalizedRight.rank;
  }
  if (typeof normalizedLeft.value === 'number' && typeof normalizedRight.value === 'number') {
    return normalizedLeft.value - normalizedRight.value;
  }
  return String(normalizedLeft.value).localeCompare(String(normalizedRight.value));
};

const matchesLocalFilterValue = (itemValue: unknown, filterValue: unknown): boolean => {
  if (filterValue === undefined || filterValue === null) return true;
  if (typeof filterValue === 'string') {
    const needle = filterValue.trim().toLowerCase();
    if (!needle) return true;
    return String(itemValue ?? '').toLowerCase().includes(needle);
  }
  if (Array.isArray(filterValue)) {
    if (filterValue.length === 0) return true;
    if (Array.isArray(itemValue)) {
      const normalizedItem = itemValue.map((entry) => String(entry).toLowerCase());
      return filterValue.some((entry) => normalizedItem.includes(String(entry).toLowerCase()));
    }
    const normalized = String(itemValue ?? '').toLowerCase();
    return filterValue.some((entry) => String(entry).toLowerCase() === normalized);
  }
  if (typeof filterValue === 'number') return Number(itemValue) === filterValue;
  if (typeof filterValue === 'boolean') return Boolean(itemValue) === filterValue;
  return stableSerialize(itemValue) === stableSerialize(filterValue);
};

const toNumericValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const matchesFilterPredicate = (
  key: string,
  itemValue: unknown,
  filterValue: unknown
): boolean => {
  const normalizedKey = key.trim().toLowerCase();
  if (normalizedKey.startsWith('min')) {
    const threshold = toNumericValue(filterValue);
    const candidate = toNumericValue(itemValue);
    if (threshold !== null && candidate !== null) return candidate >= threshold;
  }
  if (normalizedKey.startsWith('max')) {
    const threshold = toNumericValue(filterValue);
    const candidate = toNumericValue(itemValue);
    if (threshold !== null && candidate !== null) return candidate <= threshold;
  }
  return matchesLocalFilterValue(itemValue, filterValue);
};

// --- Le Hook Principal ---

// While `liveRefresh` is on (e.g. an actively-running campaign view), re-query on
// this cadence so rows the agent creates/scores keep filling the table.
const LIVE_REFRESH_INTERVAL_MS = 4000;

export interface UseInventoryListProps<K extends InventoryEntityType> {
  entityType: K;
  pageSize?: number;
  initialFilters?: Partial<InventoryRegistry[K]['filters']>;
  lockedFilters?: Partial<InventoryRegistry[K]['filters']>;
  scopedIds?: string[] | null;
  additionalSearchText?: Readonly<Record<string, string>>;
  sortOptions: { label: string; field: string; localAccessor?: (item: InventoryEntityState<K>) => unknown }[];
  filterOptions?: { key: string; label: string; localAccessor?: (item: InventoryEntityState<K>) => unknown }[];
  disableManualRefresh?: boolean;
  onManualRefresh?: () => void | Promise<void>;
  /** When true, periodically re-query so a live/streaming view (e.g. a running campaign) keeps filling. */
  liveRefresh?: boolean;
}

export function useInventoryList<K extends InventoryEntityType>({
  entityType,
  pageSize = 60,
  initialFilters,
  lockedFilters,
  scopedIds,
  additionalSearchText,
  sortOptions,
  filterOptions,
  disableManualRefresh,
  onManualRefresh,
  liveRefresh = false,
}: UseInventoryListProps<K>) {
  const { kernel } = useKernel();
  const slice = useKernelStore(state => state.inventory[entityType]);
  const registry = useKernelStore(state => state.inventory[entityType].registry);
  const isIdsMode = Array.isArray(scopedIds);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lockedFiltersRecord = useMemo(() => ((lockedFilters || {}) as Record<string, unknown>), [lockedFilters]);
  const lockedFiltersSignature = useMemo(() => stableSerialize(lockedFiltersRecord), [lockedFiltersRecord]);
  const normalizedLockedFilters = useMemo(() => lockedFiltersRecord, [lockedFiltersSignature]);
  const initialFiltersSignature = useMemo(() => stableSerialize((initialFilters || {}) as Record<string, unknown>), [initialFilters]);
  
  const hasLockedFilters = useMemo(() => Object.keys(normalizedLockedFilters).some((key) => normalizedLockedFilters[key] !== undefined), [normalizedLockedFilters]);

  const normalizedScopedIds = useMemo(() => Array.from(new Set((Array.isArray(scopedIds) ? scopedIds : []).filter((id): id is string => typeof id === 'string').map((id) => id.trim()).filter((id) => id.length > 0))), [scopedIds]);
  const scopedIdsSignature = useMemo(() => stableSerialize(normalizedScopedIds), [normalizedScopedIds]);

  const [localFilters, setLocalFilters] = useState<Record<string, unknown>>(() => ({
    ...((initialFilters || {}) as Record<string, unknown>),
    ...normalizedLockedFilters,
  }));
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [localPagination, setLocalPagination] = useState<Pagination<K> | null>(null);
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [manualRefreshIsLoading, setManualRefreshIsLoading] = useState(false);
  const [idsHydrationIsLoading, setIdsHydrationIsLoading] = useState(false);
  const [idsVisibleCount, setIdsVisibleCount] = useState<number>(0);

  // Live-refresh refs (read inside the interval without re-creating it each render).
  const liveRefreshFiltersRef = useRef<Record<string, unknown>>(localFilters);
  const localOrderLengthRef = useRef<number>(0);
  const localIsLoadingRef = useRef<boolean>(false);
  useLayoutEffect(() => {
    liveRefreshFiltersRef.current = localFilters;
    localOrderLengthRef.current = localOrder.length;
    localIsLoadingRef.current = localIsLoading;
  }, [localFilters, localOrder.length, localIsLoading]);
  const liveRefreshInFlightRef = useRef<boolean>(false);

  const idsHydrationAttemptedRef = useRef<Set<string>>(new Set());
  const sliceLastActivityRef = useRef<number>(slice.lastActivity);
  const sliceLastCreateActivity = slice.lastCreateActivity ?? 0;
  const requeriedCreateActivityRef = useRef<number>(sliceLastCreateActivity);
  const requeryWindowOpenedAtRef = useRef<number>(0);
  const localLimitRef = useRef<number>(pageSize);
  const localQueryCacheRef = useRef<Map<string, any>>(new Map());
  const latestScopedQueryRequestRef = useRef(0);

  const mergeWithLockedFilters = useCallback((candidate: Record<string, unknown>) => ({ ...candidate, ...normalizedLockedFilters }), [normalizedLockedFilters]);
  const isFilterKeyLocked = useCallback((key: string) => Object.prototype.hasOwnProperty.call(normalizedLockedFilters, key), [normalizedLockedFilters]);

  const viewFilters = localFilters;
  const viewError = isIdsMode ? null : slice.queryErrors?.[stableSerialize(viewFilters)] ?? null;
  const queryModeItems = useMemo(() => {
    const items = localOrder.map((id) => registry[id]).filter((entry): entry is InventoryEntityState<K> => Boolean(entry));
    const { sortBy, sortDir } = viewFilters as Record<string, unknown>;
    // Only with an explicit direction: without one the server picked it, and guessing could flip it.
    if (typeof sortBy !== 'string' || !sortBy || (sortDir !== 'asc' && sortDir !== 'desc')) return items;
    return sortItemsLocally(items, sortBy, sortDir, sortOptions.find((o) => o.field === sortBy)?.localAccessor);
  }, [localOrder, registry, viewFilters, sortOptions]);
  
  // --- DÉTECTION DES FILTRES ET TRIS LOCAUX ---
  const hasActiveLocalSortOrFilter = useMemo(() => {
    if (!isIdsMode) return false;
    const filters = viewFilters as Record<string, unknown>;
    
    const searchNeedle = typeof filters.search === 'string' ? filters.search.trim() : '';
    if (searchNeedle) return true;
    
    const sortBy = typeof filters.sortBy === 'string' ? filters.sortBy : '';
    if (sortBy) return true;

    const activeFilterEntries = Object.entries(filters).filter(([key, value]) => {
      if (['search', 'sortBy', 'sortDir', 'focusIds', 'ids'].includes(key)) return false;
      if (value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0) || (Array.isArray(value) && value.length === 0)) return false;
      return true;
    });
    
    return activeFilterEntries.length > 0;
  }, [isIdsMode, viewFilters]);

  const visibleScopedIds = useMemo(() => isIdsMode ? normalizedScopedIds.slice(0, Math.max(1, idsVisibleCount)) : [], [idsVisibleCount, isIdsMode, normalizedScopedIds]);
  
  // --- RÉSOLUTION INTELLIGENTE DES IDs À CHARGER ---
  const missingScopedIdsToFetch = useMemo(() => {
    if (!isIdsMode) return [];
    // Si on a un tri ou un filtre, on doit charger TOUS les IDs pour pouvoir les évaluer.
    // Sinon, on ne charge que ceux qui sont actuellement visibles à l'écran.
    const targetIds = hasActiveLocalSortOrFilter ? normalizedScopedIds : visibleScopedIds;
    return targetIds.filter((id) => !registry[id]);
  }, [isIdsMode, hasActiveLocalSortOrFilter, normalizedScopedIds, visibleScopedIds, registry]);
  
  const missingScopedIdsToFetchKey = useMemo(
    () => [...missingScopedIdsToFetch].sort((left, right) => left.localeCompare(right)).join('|'),
    [missingScopedIdsToFetch]
  );

  useEffect(() => { sliceLastActivityRef.current = slice.lastActivity; }, [slice.lastActivity]);

  const runScopedQuery = useCallback(async (nextFilters: Record<string, unknown>, offset = 0, append = false, force = false, limitOverride?: number, silent = false) => {
    if (isIdsMode) return;
    const requestId = ++latestScopedQueryRequestRef.current;
    const finalFilters = mergeWithLockedFilters(nextFilters);
    const limit = Math.max(1, limitOverride ?? localLimitRef.current);
    const signature = computeLocalTableQuerySignature(entityType, finalFilters, limit, offset);
    const cachedResult = localQueryCacheRef.current.get(signature);
    const errorKey = stableSerialize(finalFilters);
    const retryFailed = Boolean(kernel.current?.getInventory?.()[entityType]?.queryErrors?.[errorKey]);

    if (!force && !retryFailed && cachedResult) {
      const isExpired = Date.now() - cachedResult.timestamp > LOCAL_TABLE_CACHE_TTL_MS;
      const hasEntityChanged = sliceLastActivityRef.current > cachedResult.lastActivity;
      if (isExpired || hasEntityChanged) {
        localQueryCacheRef.current.delete(signature);
      } else {
        setLocalFilters(cachedResult.filters);
        setLocalPagination(cachedResult.pagination);
        setLocalOrder((prev) => append ? [...prev, ...cachedResult.ids.filter((id: string) => !new Set(prev).has(id))] : cachedResult.ids);
        if (!silent) setLocalIsLoading(false);
        return;
      }
    }

    if (!silent) setLocalIsLoading(true);
    let result;
    try {
      result = await kernel.current?.queryInventory(entityType, { filters: finalFilters as any, limit, offset, force: force || retryFailed });
    } catch {
      result = { ids: [], pagination: null, error: 'Query failed' };
    }
    if (requestId !== latestScopedQueryRequestRef.current) return;

    if (!result) { if (!silent) setLocalIsLoading(false); return; }
    if (result.error) {
      localQueryCacheRef.current.delete(signature);
      kernel.current?.setInventorySlice(entityType, prev => ({ ...prev, queryErrors: { ...prev.queryErrors, [errorKey]: 'load_failed' } }));
      if (!silent) setLocalIsLoading(false);
      return;
    }
    // Aborted/superseded kernel queries return no pagination, not a successful empty page.
    if (!result.pagination) { if (!silent) setLocalIsLoading(false); return; }
    kernel.current?.setInventorySlice(entityType, prev => {
      if (!prev.queryErrors?.[errorKey]) return prev;
      const queryErrors = { ...prev.queryErrors };
      delete queryErrors[errorKey];
      return { ...prev, queryErrors };
    });
    if (limitOverride === undefined && result.pagination?.limit) localLimitRef.current = result.pagination.limit;

    localQueryCacheRef.current.set(signature, {
      ids: result.ids,
      pagination: result.pagination,
      filters: finalFilters,
      timestamp: Date.now(),
      lastActivity: kernel.current?.getInventory?.()[entityType]?.lastActivity ?? sliceLastActivityRef.current ?? Date.now(),
    });

    setLocalFilters(finalFilters);
    setLocalPagination(result.pagination);
    setLocalOrder((prev) => append ? [...prev, ...result.ids.filter((id) => !new Set(prev).has(id))] : result.ids);
    if (!silent) setLocalIsLoading(false);
  }, [entityType, isIdsMode, kernel, mergeWithLockedFilters]);

  const scopedSeedSignature = useMemo(() => `${initialFiltersSignature}|${lockedFiltersSignature}|${isIdsMode ? scopedIdsSignature : 'query'}`, [initialFiltersSignature, lockedFiltersSignature, isIdsMode, scopedIdsSignature]);

  const setFilters = useCallback((next: Record<string, unknown>) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const merged = mergeWithLockedFilters(next);
    if (JSON.stringify(merged) !== JSON.stringify(viewFilters)) {
      const filterCount = Object.entries(next).filter(([key, value]) =>
        !['search', 'sortBy', 'sortDir', 'offset', 'limit'].includes(key) && !isFilterKeyLocked(key) && value != null && value !== '' && (!Array.isArray(value) || value.length > 0)).length;
      kernel.current?.beginSourceAction?.({ action: 'filter', origin: 'human', data: { entityType, filterCount } });
    }
    setLocalFilters(merged);
    if (!isIdsMode) {
      void runScopedQuery(merged, 0, false);
    }
  }, [entityType, isIdsMode, mergeWithLockedFilters, runScopedQuery, viewFilters, isFilterKeyLocked, kernel]);

  useEffect(() => {
    latestScopedQueryRequestRef.current += 1;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const seededFilters = mergeWithLockedFilters({ ...((initialFilters || {}) as Record<string, unknown>) });
    localLimitRef.current = pageSize;
    // A changed table scope deliberately resets its cached local projection.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIdsVisibleCount(pageSize);
    setLocalFilters(seededFilters);
    setLocalOrder([]);
    setLocalPagination(null);
    if (!isIdsMode) void runScopedQuery(seededFilters, 0, false);
    return () => {
      latestScopedQueryRequestRef.current += 1;
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [isIdsMode, mergeWithLockedFilters, runScopedQuery, scopedSeedSignature, pageSize]);

  // Live refresh: while `liveRefresh` is on (an actively-running campaign view),
  // re-query on an interval so rows the agent creates/scores keep filling the
  // table. Preserves the loaded extent so the user's scroll position is kept.
  useEffect(() => {
    if (!liveRefresh || isIdsMode) return;
    const intervalId = setInterval(() => {
      if (localIsLoadingRef.current || liveRefreshInFlightRef.current) return;
      liveRefreshInFlightRef.current = true;
      const extent = Math.max(pageSize, localOrderLengthRef.current);
      void runScopedQuery(liveRefreshFiltersRef.current, 0, false, true, extent, true)
        .finally(() => { liveRefreshInFlightRef.current = false; });
    }, LIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [liveRefresh, isIdsMode, pageSize, runScopedQuery]);

  // A CREATE expired the kernel query cache of this type, so the list re-queries itself: the client
  // cannot know where a created entity lands in a server-filtered list, only that its list is stale.
  // The window is never reopened, so once a first re-query has fired, an isolated creation after a
  // lull goes out on the next tick; clearing the ref on fire would put it back behind a full window.
  useEffect(() => {
    if (isIdsMode || sliceLastCreateActivity <= requeriedCreateActivityRef.current) return;
    requeryWindowOpenedAtRef.current ||= Date.now();

    let timerId: ReturnType<typeof setTimeout>;
    const requery = () => {
      if (localIsLoadingRef.current || liveRefreshInFlightRef.current) {
        requeryWindowOpenedAtRef.current = Date.now();
        timerId = setTimeout(requery, LIVE_REFRESH_INTERVAL_MS);
        return;
      }
      requeriedCreateActivityRef.current = sliceLastCreateActivity;
      requeryWindowOpenedAtRef.current = Date.now();
      liveRefreshInFlightRef.current = true;
      const extent = Math.max(pageSize, localOrderLengthRef.current);
      void runScopedQuery(liveRefreshFiltersRef.current, 0, false, true, extent, true)
        .finally(() => { liveRefreshInFlightRef.current = false; });
    };
    timerId = setTimeout(requery, requeryWindowOpenedAtRef.current + LIVE_REFRESH_INTERVAL_MS - Date.now());

    return () => clearTimeout(timerId);
  }, [isIdsMode, pageSize, runScopedQuery, sliceLastCreateActivity]);

  const handleSearchChange = useCallback((val: string) => {
    if (isFilterKeyLocked('search')) return;
    latestScopedQueryRequestRef.current += 1;
    if (val !== String(viewFilters.search ?? '')) kernel.current?.beginSourceAction?.({ action: 'search', origin: 'human', data: { entityType, hasQuery: val.trim().length > 0 } });
    const nextFilters = mergeWithLockedFilters({ ...viewFilters, search: val });
    setLocalFilters(nextFilters);
    if (isIdsMode) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => void runScopedQuery(nextFilters, 0, false), 350);
  }, [entityType, isFilterKeyLocked, isIdsMode, mergeWithLockedFilters, runScopedQuery, viewFilters, kernel]);

  const handleSortChange = useCallback((field: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const filters = viewFilters as any;
    const isSame = filters.sortBy === field;
    const nextDir = isSame && filters.sortDir === 'desc' ? 'asc' : 'desc';
    const nextFilters = mergeWithLockedFilters({
      ...filters,
      sortBy: isFilterKeyLocked('sortBy') ? filters.sortBy : field,
      sortDir: isFilterKeyLocked('sortDir') ? filters.sortDir : nextDir,
    });
    setLocalFilters(nextFilters);
    if (!isIdsMode) void runScopedQuery(nextFilters, 0, false);
  }, [isFilterKeyLocked, isIdsMode, mergeWithLockedFilters, runScopedQuery, viewFilters]);

  const handleManualRefresh = useCallback(() => {
    if (viewError && !isIdsMode) {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      void runScopedQuery(viewFilters, 0, false, true);
      return;
    }
    if (disableManualRefresh) return;
    if (onManualRefresh) {
      setManualRefreshIsLoading(true);
      Promise.resolve(onManualRefresh()).catch(() => undefined).finally(() => setManualRefreshIsLoading(false));
      return;
    }
    if (isIdsMode) {
      if (!kernel.current?.ensureInventoryIds || missingScopedIdsToFetch.length === 0) return;
      const requestSignature = `${entityType}|${missingScopedIdsToFetchKey}`;
      idsHydrationAttemptedRef.current.delete(requestSignature);
      setManualRefreshIsLoading(true);
      void kernel.current.ensureInventoryIds(entityType, missingScopedIdsToFetch, { force: true }).catch(() => undefined).finally(() => setManualRefreshIsLoading(false));
      return;
    }
    void runScopedQuery(viewFilters, 0, false, true);
  }, [disableManualRefresh, entityType, isIdsMode, kernel, missingScopedIdsToFetch, missingScopedIdsToFetchKey, onManualRefresh, runScopedQuery, viewFilters, viewError]);

  useEffect(() => {
    if (!isIdsMode || !missingScopedIdsToFetchKey) {
      // Hydration ends when the external inventory supplies all requested rows.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIdsHydrationIsLoading((prev) => (prev ? false : prev));
      return;
    }
    const requestSignature = `${entityType}|${missingScopedIdsToFetchKey}`;
    if (idsHydrationAttemptedRef.current.has(requestSignature)) return;
    if (!kernel.current?.ensureInventoryIds) return;

    idsHydrationAttemptedRef.current.add(requestSignature);
    let cancelled = false;
    setIdsHydrationIsLoading(true);
    void kernel.current.ensureInventoryIds(entityType, missingScopedIdsToFetch, { force: false }).catch(() => undefined).finally(() => { if (!cancelled) setIdsHydrationIsLoading(false); });
    return () => { cancelled = true; };
  }, [entityType, isIdsMode, kernel, missingScopedIdsToFetch, missingScopedIdsToFetchKey]);

  useEffect(() => {
    if (isIdsMode || entityType !== 'Project') return;
    if (typeof window === 'undefined') return;

    const handleProjectGridReload = () => {
      void runScopedQuery(viewFilters, 0, false, true);
    };

    window.addEventListener('fater:project-copied', handleProjectGridReload);
    window.addEventListener('fater:project-list-reload', handleProjectGridReload);
    return () => {
      window.removeEventListener('fater:project-copied', handleProjectGridReload);
      window.removeEventListener('fater:project-list-reload', handleProjectGridReload);
    };
  }, [entityType, isIdsMode, runScopedQuery, viewFilters]);

  const idsModeResolvedItems = useMemo(() => isIdsMode ? normalizedScopedIds.map((id) => registry[id]).filter((entry): entry is InventoryEntityState<K> => Boolean(entry)) : [], [isIdsMode, normalizedScopedIds, registry]);

  const idsModeFilteredSortedItems = useMemo(() => {
    if (!isIdsMode) return [];
    const filters = viewFilters as Record<string, unknown>;
    const searchNeedle = typeof filters.search === 'string' ? filters.search.trim().toLowerCase() : '';
    const activeFilterEntries = Object.entries(filters).filter(([key, value]) => {
      if (['search', 'sortBy', 'sortDir', 'focusIds', 'ids'].includes(key)) return false;
      if (value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0) || (Array.isArray(value) && value.length === 0)) return false;
      return true;
    });

    const filteredItems = idsModeResolvedItems.filter((item) => {
      const record = item as unknown as Record<string, unknown>;
      if (searchNeedle && ![...collectSearchTerms(item), additionalSearchText?.[item.id] ?? ''].join(' ').toLowerCase().includes(searchNeedle)) return false;
      for (const [key, value] of activeFilterEntries) {
        const option = filterOptions?.find(o => o.key === key);
        const candidateValue = option?.localAccessor ? option.localAccessor(item) : resolveRecordValue(record, key);
        if (!matchesFilterPredicate(key, candidateValue, value)) return false;
      }
      return true;
    });

    const sortBy = typeof filters.sortBy === 'string' ? filters.sortBy : '';
    if (!sortBy) return filteredItems;

    const sortDirection = filters.sortDir === 'asc' ? 'asc' : LOCAL_SORT_DEFAULT_DIR;
    return sortItemsLocally(filteredItems, sortBy, sortDirection, sortOptions.find((o) => o.field === sortBy)?.localAccessor);
  }, [additionalSearchText, filterOptions, idsModeResolvedItems, isIdsMode, sortOptions, viewFilters]);

  const idsModeVisibleItems = useMemo(() => isIdsMode ? idsModeFilteredSortedItems.slice(0, Math.max(1, idsVisibleCount)) : [], [idsModeFilteredSortedItems, idsVisibleCount, isIdsMode]);

  const idsModePagination = useMemo((): Pagination<K> | null => {
    if (!isIdsMode) return null;
    const total = idsModeFilteredSortedItems.length;
    const visibleCount = idsModeVisibleItems.length;
    const limit = pageSize;
    return { mode: visibleCount > limit ? 'append' : 'replace', limit, offset: Math.max(0, visibleCount - limit), total, hasMore: visibleCount < total } as Pagination<K>;
  }, [idsModeFilteredSortedItems.length, idsModeVisibleItems.length, isIdsMode, pageSize]);

  const handleReachBottom = useCallback(() => {
    if (isIdsMode) {
      if (idsModePagination?.hasMore) setIdsVisibleCount((prev) => prev + localLimitRef.current);
      return;
    }
    if (!viewError && !localIsLoading && localPagination?.hasMore) {
      void runScopedQuery(viewFilters, (localPagination.offset ?? 0) + (localPagination.limit ?? localLimitRef.current), true);
    }
  }, [idsModePagination?.hasMore, isIdsMode, localIsLoading, localPagination, runScopedQuery, viewFilters, viewError]);

  return {
    viewItems: isIdsMode ? idsModeVisibleItems : queryModeItems,
    viewError,
    viewIsLoading: isIdsMode ? idsHydrationIsLoading : localIsLoading,
    refreshIsLoading: isIdsMode ? (manualRefreshIsLoading || idsHydrationIsLoading) : localIsLoading,
    viewPagination: isIdsMode ? idsModePagination : localPagination,
    viewFilters,
    isIdsMode,
    hasLockedFilters,
    isFilterKeyLocked,
    mergeWithLockedFilters,
    setFilters,
    handleSearchChange,
    handleSortChange,
    handleManualRefresh,
    handleReachBottom,
  };
}
